// P3-008: Match detection for 7x5 board
// Detects all horizontal and vertical 3+ matches, handles overlapping (T/L/cross shapes)

import type { ElementType } from '@/shared/types/common';
import type { Tile, Match, TilePosition } from '../types/board';
import { BOARD_COLS, BOARD_ROWS, MIN_MATCH } from '../data/board-constants';

/**
 * Detect all matches on the board.
 * Returns Match[] with deduplicated tiles for overlapping matches.
 */
export function detectAll(tiles: (Tile | null)[][]): Match[] {
  const matches: Match[] = [];

  // Scan horizontal matches (row by row)
  for (let y = 0; y < BOARD_ROWS; y++) {
    let x = 0;
    while (x < BOARD_COLS) {
      const tile = tiles[x]?.[y];
      if (!tile) {
        x++;
        continue;
      }

      const element = tile.element;
      const run: Tile[] = [tile];

      let nx = x + 1;
      while (nx < BOARD_COLS) {
        const nextTile = tiles[nx]?.[y];
        if (nextTile && nextTile.element === element) {
          run.push(nextTile);
          nx++;
        } else {
          break;
        }
      }

      if (run.length >= MIN_MATCH) {
        matches.push({
          tiles: run,
          element,
          isHorizontal: true,
        });
      }

      x = nx;
    }
  }

  // Scan vertical matches (column by column)
  for (let x = 0; x < BOARD_COLS; x++) {
    let y = 0;
    while (y < BOARD_ROWS) {
      const tile = tiles[x]?.[y];
      if (!tile) {
        y++;
        continue;
      }

      const element = tile.element;
      const run: Tile[] = [tile];

      let ny = y + 1;
      while (ny < BOARD_ROWS) {
        const nextTile = tiles[x]?.[ny];
        if (nextTile && nextTile.element === element) {
          run.push(nextTile);
          ny++;
        } else {
          break;
        }
      }

      if (run.length >= MIN_MATCH) {
        matches.push({
          tiles: run,
          element,
          isHorizontal: false,
        });
      }

      y = ny;
    }
  }

  return matches;
}

/**
 * Quick check: does the board have any matches?
 */
export function hasAnyMatch(tiles: (Tile | null)[][]): boolean {
  // Check horizontal
  for (let y = 0; y < BOARD_ROWS; y++) {
    let count = 1;
    for (let x = 1; x < BOARD_COLS; x++) {
      const prev = tiles[x - 1]?.[y];
      const curr = tiles[x]?.[y];
      if (prev && curr && prev.element === curr.element) {
        count++;
        if (count >= MIN_MATCH) return true;
      } else {
        count = 1;
      }
    }
  }

  // Check vertical
  for (let x = 0; x < BOARD_COLS; x++) {
    let count = 1;
    for (let y = 1; y < BOARD_ROWS; y++) {
      const prev = tiles[x]?.[y - 1];
      const curr = tiles[x]?.[y];
      if (prev && curr && prev.element === curr.element) {
        count++;
        if (count >= MIN_MATCH) return true;
      } else {
        count = 1;
      }
    }
  }

  return false;
}

/**
 * Check if any valid swap exists that would produce a match.
 * Returns false if the board is in a dead-lock state.
 */
export function hasValidMove(tiles: (Tile | null)[][]): boolean {
  // Try every possible swap of adjacent tiles
  for (let x = 0; x < BOARD_COLS; x++) {
    for (let y = 0; y < BOARD_ROWS; y++) {
      const tile = tiles[x]?.[y];
      if (!tile || tile.locked) continue;

      // Try swap right
      if (x + 1 < BOARD_COLS) {
        const right = tiles[x + 1]?.[y];
        if (right && !right.locked) {
          if (wouldProduceMatch(tiles, { x, y }, { x: x + 1, y })) {
            return true;
          }
        }
      }

      // Try swap down
      if (y + 1 < BOARD_ROWS) {
        const below = tiles[x]?.[y + 1];
        if (below && !below.locked) {
          if (wouldProduceMatch(tiles, { x, y }, { x, y: y + 1 })) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Check if swapping two positions would produce at least one match.
 */
export function wouldProduceMatch(
  tiles: (Tile | null)[][],
  pos1: TilePosition,
  pos2: TilePosition,
): boolean {
  const tile1 = tiles[pos1.x]?.[pos1.y];
  const tile2 = tiles[pos2.x]?.[pos2.y];
  if (!tile1 || !tile2) return false;

  // Create a temporary copy with the swap applied
  // We only need to check around the two swapped positions
  return (
    checkMatchAtPosition(tiles, pos1, tile2.element, pos2) ||
    checkMatchAtPosition(tiles, pos2, tile1.element, pos1)
  );
}

/**
 * Check if placing an element at a position creates a match,
 * considering that the `swappedFrom` position now has a different element.
 */
function checkMatchAtPosition(
  tiles: (Tile | null)[][],
  pos: TilePosition,
  element: ElementType,
  swappedFrom: TilePosition,
): boolean {
  const { x, y } = pos;

  // Helper to get the element at a position, accounting for the swap
  const getElement = (cx: number, cy: number): ElementType | null => {
    if (cx === pos.x && cy === pos.y) return element;
    if (cx === swappedFrom.x && cy === swappedFrom.y) {
      // The other position now has the original element of `pos`
      return tiles[pos.x]?.[pos.y]?.element ?? null;
    }
    return tiles[cx]?.[cy]?.element ?? null;
  };

  // Check horizontal run through (x, y)
  let hCount = 1;
  // Count left
  for (let cx = x - 1; cx >= 0; cx--) {
    if (getElement(cx, y) === element) hCount++;
    else break;
  }
  // Count right
  for (let cx = x + 1; cx < BOARD_COLS; cx++) {
    if (getElement(cx, y) === element) hCount++;
    else break;
  }
  if (hCount >= MIN_MATCH) return true;

  // Check vertical run through (x, y)
  let vCount = 1;
  // Count up
  for (let cy = y - 1; cy >= 0; cy--) {
    if (getElement(x, cy) === element) vCount++;
    else break;
  }
  // Count down
  for (let cy = y + 1; cy < BOARD_ROWS; cy++) {
    if (getElement(x, cy) === element) vCount++;
    else break;
  }
  if (vCount >= MIN_MATCH) return true;

  return false;
}

/**
 * Get all unique tile positions from an array of matches (for removal).
 * Deduplicates tiles that appear in multiple matches (T/L/cross shapes).
 */
export function getUniquePositions(matches: Match[]): TilePosition[] {
  const seen = new Set<string>();
  const positions: TilePosition[] = [];

  for (const match of matches) {
    for (const tile of match.tiles) {
      const key = `${tile.x},${tile.y}`;
      if (!seen.has(key)) {
        seen.add(key);
        positions.push({ x: tile.x, y: tile.y });
      }
    }
  }

  return positions;
}
